import React, { useCallback, useContext, useState } from 'react';
import CollectionList from '../../../../../../../_Common/Components/BusinessCollectionComponents/CollectionList/CollectionList';
import { IClientBusinessModel } from '../../../../../../../../../Types/ClientModelTypes';
import clientAvenyModel from '../../../../../../../../../Model/clientAvenyModel';
import { IAssociationListConfig } from '../../../../../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { GridEventListener } from '@mui/x-data-grid-pro';
import BokforderVerificationWizardStep2, {
  IVerRowWithPeriod,
} from '../BokforderVerificationWizardStep2/BokforderVerificationWizardStep2';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import { EDTextEdit } from '../../../../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDButton from '../../../../../../../../../BaseComponents/Buttons/Button/EDButton';
import { IUIUrval } from '../../../../../../../../../Types/UrvalTypes';
import { BokforderVerificationQuery_node_TEDEKBokfver } from '../../Queries/_QueryTypes/BokforderVerificationQuery';
import EDTypography from '../../../../../../../../../BaseComponents/Typography/EDTypography';
import UrvalHitIndicator from '../../../../../../../../../Components/Urval/Components/HitIndicator/UrvalHitIndicator';
import {
  IVerRow,
  makeVerificationUrval,
} from '../../Utils/BokforderCreateDialogUtils';
import EDKeybind from '../../../../../../../../../BaseComponents/Keybinds/EDKeybind';
import { ISelectedVerRow } from '../../BokforderCreateDialog';
import BokforderContext from '../../../BokforderContext/BokforderContext';
import { BokforderWrapperContext } from '../../../../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import { findFormatter } from '../../../../../../../../../Utils/Formatters/FindFormatter';
import { ParseResult } from '../../../../../../../../../Utils/Formatters/FormatterTypes';

const verificationModel: IClientBusinessModel =
  clientAvenyModel.EK.BusinessModels.EKBokfver;

const verificationConfig: IAssociationListConfig = {
  customDefaultColumns: [
    { attributeDef: 'VER_NR', caption: 'Vernr', header: true },
    { attributeDef: 'DATUM', caption: 'Bokf.datum' },
    { attributeDef: 'TEXT', caption: 'Text' },
    { attributeDef: 'VERKAT_KOD', caption: 'Ver.kategori' },
    { attributeDef: 'Summa_Debet', caption: 'Belopp' },
  ],
  customBusinessConfig: { dataDependencies: ['HarAndringsVerifikation'] },
  defaultVisningstyp: 'tabell',
  inCard: true,
  autoHeight: false,
};

const dateYearFormatter = findFormatter('TEDFormatDateYear', 'Date');

interface IProps {
  chosenRows: IVerRow[];
  includeRowText: boolean;
  periodChosenRows: IVerRowWithPeriod[];
  selectedVerRow: ISelectedVerRow | undefined;
  setChosenRows: React.Dispatch<React.SetStateAction<IVerRow[]>>;
  setIncludeRowText: React.Dispatch<React.SetStateAction<boolean>>;
  setPeriodChosenRows: React.Dispatch<
    React.SetStateAction<IVerRowWithPeriod[]>
  >;
  setVerification: React.Dispatch<
    React.SetStateAction<BokforderVerificationQuery_node_TEDEKBokfver | null>
  >;
  setSelectedVerRow: React.Dispatch<
    React.SetStateAction<ISelectedVerRow | undefined>
  >;
  step: number;
}

const BokforderVerificationWizard: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { setSelectedVerRow } = props;
  const wrapperContext = useContext(BokforderWrapperContext);
  const bokforderContext = useContext(BokforderContext);
  const [bokfAr, setBokfAr] = useState<string>(
    bokforderContext.urlParams.bokfar ?? `${new Date().getFullYear()}`
  );
  const bokfArParsed = dateYearFormatter.parser.parse(bokfAr);
  const [bokfArTouched, setBokfArTouched] = useState(false);

  const [selection, setSelection] = useState<IUIUrval | undefined>(
    bokforderContext.urlParams.vernr && bokforderContext.urlParams.bokfar
      ? makeVerificationUrval(
          bokforderContext.urlParams.bokfar,
          bokforderContext.urlParams.vernr
        )
      : undefined
  );
  const [verNr, setVerNr] = useState<string>(
    bokforderContext.urlParams.vernr ?? ''
  );

  const onRowClick: GridEventListener<'rowClick'> = (
    params,
    event,
    details
  ) => {
    if (
      props.selectedVerRow?.id === params.id.toString() &&
      event.ctrlKey === true
    ) {
      props.setSelectedVerRow({
        id: '',
        verkatKod: '',
        harAndringsVerifikation: false,
      });
    } else {
      props.setSelectedVerRow({
        id: params.id.toString(),
        verkatKod: params.row?.VERKAT_KOD ?? '',
        harAndringsVerifikation:
          params.row?.collectionData?.HarAndringsVerifikation,
      });
    }
  };

  const onSetSelection = useCallback(() => {
    if (bokfArParsed.result === ParseResult.OK) {
      const newSelection = makeVerificationUrval(bokfAr, verNr);
      setSelectedVerRow({
        id: '',
        verkatKod: '',
        harAndringsVerifikation: false,
      });
      setSelection(newSelection);
    }
  }, [bokfAr, bokfArParsed.result, setSelectedVerRow, verNr]);

  const onListQueryCompleted = useCallback(
    (data: any) => {
      if (data?.db?.EKBokfver_All?.length === 1) {
        const ver = data?.db?.EKBokfver_All?.[0];
        setSelectedVerRow({
          id: ver?.id,
          verkatKod: ver?.VERKAT_KOD,
          harAndringsVerifikation: ver?.HarAndringsVerifikation,
        });
      }
    },
    [setSelectedVerRow]
  );

  return (
    <>
      {props.step === 1 && (
        <EDBox sx={{ display: 'flex', flexFlow: 'column', height: '100%' }}>
          <EDTypography variant="h6" mb={1}>
            Sök efter verifikation för hantering
          </EDTypography>
          <EDBox
            sx={{
              display: 'flex',
              flexFlow: 'row nowrap',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {!wrapperContext.currentUser ||
            (bokforderContext.limitedCreateMode &&
              !wrapperContext.isEconomyPersonnel) ? (
              <>
                <EDTypography mr={1} sx={{ display: 'flex' }}>
                  <EDTypography bold mr={0.5}>
                    Bokföringsår:
                  </EDTypography>
                  {bokfAr}
                </EDTypography>
                <EDTypography ml={1} sx={{ display: 'flex' }}>
                  <EDTypography bold mr={0.5}>
                    Verifikationsnummer:
                  </EDTypography>
                  {verNr}
                </EDTypography>
              </>
            ) : (
              <>
                <EDBox
                  sx={theme => ({
                    margin: theme => '0 ' + theme.spacing(1),
                    marginLeft: theme => theme.spacing(0),
                    [theme.breakpoints.up('md')]: { width: '15%' },
                  })}
                >
                  <EDTextEdit
                    allMargins="none"
                    fullWidth
                    label="Bokföringsår"
                    onChange={newValue => {
                      if (newValue == null || newValue === '') {
                        setBokfAr('');
                      } else {
                        setBokfAr(newValue);
                      }
                    }}
                    value={bokfAr}
                    onBlur={() => {
                      setBokfArTouched(true);
                      if (bokfArParsed.result === ParseResult.OK) {
                        setBokfAr(
                          dateYearFormatter.displayer.display(
                            bokfArParsed.value
                          )
                        );
                      }
                    }}
                    testId="bokforingsar-input"
                  />
                </EDBox>
                <EDBox
                  sx={theme => ({
                    margin: theme => '0 ' + theme.spacing(1),
                    [theme.breakpoints.up('md')]: { width: '15%' },
                  })}
                >
                  <EDTextEdit
                    allMargins="none"
                    fullWidth
                    label="Verifikationsnummer"
                    onChange={newValue => {
                      if (newValue == null || newValue === '') {
                        setVerNr('');
                      } else {
                        setVerNr(newValue);
                      }
                    }}
                    value={verNr}
                    testId="verifikationsnummer-input"
                  />
                </EDBox>
                <EDKeybind keymap="Enter" onKeyDown={onSetSelection} global>
                  <EDButton
                    label={'Sök'}
                    onClick={onSetSelection}
                    mx={1}
                    disabled={
                      bokfArTouched && bokfArParsed.result !== ParseResult.OK
                    }
                    disabledReason="Ange ett giltigt bokföringsår"
                    testId="verifikation-sok"
                  />
                </EDKeybind>
                <UrvalHitIndicator
                  collection={verificationModel}
                  urval={
                    bokfArParsed.result === ParseResult.OK &&
                    typeof bokfArParsed.value === 'string'
                      ? makeVerificationUrval(bokfArParsed.value, verNr)
                      : undefined
                  }
                  skip={bokfArParsed.result !== ParseResult.OK}
                />
              </>
            )}
          </EDBox>
          <EDBox sx={{ height: 'calc(100% - 55px)' }}>
            <CollectionList
              collectionName={verificationModel.CollectionName}
              config={verificationConfig}
              disableStartWithEmptyUrval
              onRowClick={onRowClick}
              selectionGrid
              selectedId={props.selectedVerRow?.id}
              urval={selection ?? undefined}
              onCompleted={onListQueryCompleted}
            />
          </EDBox>
        </EDBox>
      )}
      {props.step === 2 && (
        <>
          <BokforderVerificationWizardStep2
            chosenRows={props.chosenRows}
            includeRowText={props.includeRowText}
            periodChosenRows={props.periodChosenRows}
            selectedVerRow={props.selectedVerRow}
            setChosenRows={props.setChosenRows}
            setIncludeRowText={props.setIncludeRowText}
            setPeriodChosenRows={props.setPeriodChosenRows}
            setVerification={props.setVerification}
          />
        </>
      )}
    </>
  );
};

export default BokforderVerificationWizard;
